import { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from '@strivelabs/ui-core';
import { manageKeyVal } from '@/services/DataService';
import { useTriggerWorkflow } from '@/hooks/useTriggerWorkflow';
import { log } from '@/lib/logger';

/**
 * Configuration for useStoreAndFetch hook
 */
export interface UseStoreAndFetchConfig {
  /** Array of entries to store in key-value store. If undefined/empty, skip store phase. */
  entries?: unknown[];
  /** Workflow instance ID to scope the key-value entries */
  workflowInstanceId: string;
  /** Bucket name to store entries (e.g., 'TASKS_TODO_BUCKET') */
  bucketName: string;
  /** Key-value app name (e.g., 'kv_task_queue') */
  appName: string;
  /** Agent short name for logging */
  agentShortName: string;
  /** Transform function to convert raw entry to store format */
  transformEntry: (entry: unknown) => unknown;
  /** When true, entries were already stored in a previous session — skip store phase entirely */
  alreadyStored?: boolean;
  /** Callback invoked after entries are successfully stored (used to persist state) */
  onStoreSuccess?: () => void;
  /**
   * When true, fire a manual workflow trigger after each entry is stored,
   * passing the row's kv_entry_key. Required for agents that no longer rely
   * on a scheduled trigger to drain the TODO bucket. Default false to
   * preserve existing schedule-driven slots (e.g. refresher).
   */
  triggerAfterStore?: boolean;
}

/**
 * Result from useStoreAndFetch hook
 */
export interface UseStoreAndFetchResult {
  /** Flag indicating if data fetching should proceed */
  shouldFetch: boolean;
  /** Flag indicating if store operations are in progress */
  isStoring: boolean;
  /** Error from store phase, if any */
  storeError: Error | null;
}

/**
 * Store-then-fetch hook for agent slot experiences.
 *
 * Implements the trigger flow pattern:
 * 1. If entries provided, store them to key-value store first
 * 2. After successful store (or if no entries), enable data fetching
 * 3. Handle errors gracefully without blocking UI
 *
 * Based on the pattern from TriggerScreen_CSV.tsx.
 *
 * @example
 * ```tsx
 * const { shouldFetch, isStoring } = useStoreAndFetch({
 *   entries: action.data?.entries,
 *   workflowInstanceId,
 *   bucketName: 'TASKS_TODO_BUCKET',
 *   appName: 'kv_task_queue',
 *   agentShortName: 'blog-post-content-refresher',
 *   transformEntry: (entry) => ({
 *     blog_post_url: entry.blog_post_url,
 *     primary_keywords: entry.primary_keywords
 *   })
 * });
 * ```
 */
export function useStoreAndFetch({
  entries,
  workflowInstanceId,
  bucketName,
  appName,
  agentShortName,
  transformEntry,
  alreadyStored = false,
  onStoreSuccess,
  triggerAfterStore = false,
}: UseStoreAndFetchConfig): UseStoreAndFetchResult {
  const { mutateAsync: triggerWorkflow } = useTriggerWorkflow();
  const [shouldFetch, setShouldFetch] = useState(false);
  const [isStoring, setIsStoring] = useState(false);
  const [storeError, setStoreError] = useState<Error | null>(null);

  // Track if we've already stored to prevent duplicate store operations
  const hasStoredRef = useRef(false);

  // Use a ref for onStoreSuccess to avoid stale closures in the async storeEntries callback.
  // During streaming, chatHistoryId may arrive (via CHAT_METADATA) after the store operation
  // has already started, so we need to always call the latest version of the callback.
  const onStoreSuccessRef = useRef(onStoreSuccess);
  onStoreSuccessRef.current = onStoreSuccess;

  // Reset stored flag when workflowInstanceId changes
  useEffect(() => {
    hasStoredRef.current = false;
  }, [workflowInstanceId]);

  const storeEntries = useCallback(async () => {
    if (!entries || entries.length === 0 || alreadyStored) {
      // No entries to store or already stored in a previous session, proceed directly to fetch
      setShouldFetch(true);
      return;
    }

    if (hasStoredRef.current) {
      // Already stored, don't store again
      return;
    }

    hasStoredRef.current = true;
    setIsStoring(true);
    setStoreError(null);

    log('info', `[${agentShortName}] Starting store phase`, {
      entryCount: entries.length,
      workflowInstanceId,
      bucketName
    });

    try {
      if (triggerAfterStore) {
        // Sequential write+trigger per entry: each row needs its own
        // kv_entry_key handed straight to its own trigger call, and on
        // trigger failure we want to clean up the just-written row before
        // touching the next one.
        for (let index = 0; index < entries.length; index++) {
          const entry = entries[index];
          const transformedData = transformEntry(entry) as Record<string, unknown>;
          const entryKey = crypto.randomUUID();

          log('info', `[${agentShortName}] Storing entry ${index + 1}/${entries.length}`, {
            data: transformedData
          });

          await manageKeyVal({
            action: 'store',
            key_val: {
              app_name: appName,
              entity: bucketName,
              workflow_instance_id: workflowInstanceId,
              key: entryKey,
              data: transformedData,
            },
          });

          try {
            await triggerWorkflow({
              trigger_workflow: {
                workflow_instance_id: workflowInstanceId,
                data: [
                  {
                    data: { ...transformedData, kv_entry_key: entryKey },
                    scenario: 'default',
                  },
                ],
              },
            });
          } catch (triggerError) {
            // Roll back the TODO row so a future trigger doesn't pick up an
            // orphaned entry we never actually kicked off.
            log('error', `[${agentShortName}] Manual trigger failed after store; rolling back TODO row`, {
              key: entryKey,
              workflowInstanceId,
              error: triggerError,
            });
            try {
              await manageKeyVal({
                action: 'delete',
                key_val: {
                  app_name: appName,
                  entity: bucketName,
                  workflow_instance_id: workflowInstanceId,
                  key: entryKey,
                },
              });
            } catch (rollbackError) {
              log('error', `[${agentShortName}] Failed to roll back TODO row after trigger failure`, {
                key: entryKey,
                workflowInstanceId,
                error: rollbackError,
              });
            }
            throw triggerError;
          }
        }
      } else {
        // Original schedule-driven path: parallel writes, no manual trigger.
        // Used by the refresher slot; throughput-sensitive, leave it alone.
        const storePromises = entries.map((entry, index) => {
          const transformedData = transformEntry(entry);

          log('info', `[${agentShortName}] Storing entry ${index + 1}/${entries.length}`, {
            data: transformedData
          });

          return manageKeyVal({
            action: 'store',
            key_val: {
              app_name: appName,
              entity: bucketName,
              workflow_instance_id: workflowInstanceId,
              key: crypto.randomUUID(),
              data: transformedData,
            },
          });
        });

        await Promise.all(storePromises);
      }

      log('info', `[${agentShortName}] Store phase complete`, {
        successCount: entries.length
      });

      toast({
        title: 'Success',
        description: `Added ${entries.length} ${entries.length === 1 ? 'entry' : 'entries'} to task queue`,
        variant: 'success',
      });

      // Notify caller so it can persist the "entries_stored" state.
      // Use ref to always call the latest callback (avoids stale closure).
      onStoreSuccessRef.current?.();

      // Store successful, enable fetching
      setShouldFetch(true);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';

      log('error', `[${agentShortName}] Store phase failed`, {
        error: errorMessage,
        entryCount: entries.length
      });

      setStoreError(error instanceof Error ? error : new Error(errorMessage));

      toast({
        title: 'Error',
        description: `Failed to add entries to task queue. Showing existing entries.`,
        variant: 'destructive',
      });

      // Even on error, proceed to fetch existing entries (graceful degradation)
      setShouldFetch(true);
    } finally {
      setIsStoring(false);
    }
  }, [entries, workflowInstanceId, bucketName, appName, agentShortName, transformEntry, alreadyStored, triggerAfterStore, triggerWorkflow]);

  // Execute store phase on mount or when dependencies change
  useEffect(() => {
    if (!workflowInstanceId) {
      return;
    }

    storeEntries();
  }, [storeEntries, workflowInstanceId]);

  return {
    shouldFetch,
    isStoring,
    storeError,
  };
}
